import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AreaField,
  BilingualField,
  Panel,
  Repeater,
  TextField,
  Toggle,
} from "@/components/studio/Fields";
import { errorMessage } from "@/lib/api";
import { generateCreative, getPlacements, ownerCampaigns, removeCampaign, saveCampaign } from "@/lib/site";

/* Two halves. Above: the space you sell on your own pages. Below: the copy for
   space you buy on someone else's. They share a tab because they share a
   subject, and nothing else. */

const SlotEditor = ({ item, patch, placements }) => (
  <>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <TextField
        label="Nom interne"
        hint="Pour vous y retrouver. Jamais affiché."
        value={item.label}
        onChange={(value) => patch({ label: value })}
        testId={`slot-label-${item.id || "new"}`}
      />
      <div className="mt-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--muted-ink)]">
          Emplacement
        </p>
        <select
          value={item.placement}
          onChange={(event) => patch({ placement: event.target.value })}
          data-testid={`slot-placement-${item.id || "new"}`}
          className="mt-1.5 h-10 w-full rounded-lg border bg-white px-2 text-[13.5px]"
          style={{ borderColor: "var(--line)" }}
        >
          {placements.map((placement) => (
            <option key={placement.id} value={placement.id}>
              {placement.label}
            </option>
          ))}
        </select>
      </div>
    </div>

    <div className="mt-4 flex gap-2">
      {[
        ["own", "Ma propre publicité"],
        ["network", "Code d’une régie"],
      ].map(([kind, label]) => (
        <button
          key={kind}
          type="button"
          onClick={() => patch({ kind })}
          aria-pressed={item.kind === kind}
          data-testid={`slot-kind-${kind}-${item.id || "new"}`}
          className="rounded-full border px-3 py-1.5 text-[12.5px]"
          style={{
            borderColor: item.kind === kind ? "var(--ink)" : "var(--line)",
            backgroundColor: item.kind === kind ? "var(--ink)" : "transparent",
            color: item.kind === kind ? "var(--paper)" : "var(--ink-soft)",
          }}
        >
          {label}
        </button>
      ))}
    </div>

    {item.kind === "network" ? (
      <AreaField
        label="Code de la régie"
        hint="Collé tel que la régie vous le donne. Il s’exécute sur la page : ne collez que du code dont vous connaissez l’origine."
        value={item.network_code}
        onChange={(value) => patch({ network_code: value })}
        rows={5}
        testId={`slot-code-${item.id || "new"}`}
      />
    ) : (
      <>
        <BilingualField
          label="Titre"
          value={item.title}
          onChange={(value) => patch({ title: value })}
          testId={`slot-title-${item.id || "new"}`}
        />
        <BilingualField
          label="Texte"
          value={item.body}
          onChange={(value) => patch({ body: value })}
          rows={2}
          testId={`slot-body-${item.id || "new"}`}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <BilingualField
            label="Bouton"
            value={item.cta}
            onChange={(value) => patch({ cta: value })}
            testId={`slot-cta-${item.id || "new"}`}
          />
          <TextField
            label="Lien"
            value={item.href}
            onChange={(value) => patch({ href: value })}
            placeholder="https://…"
            testId={`slot-href-${item.id || "new"}`}
          />
          <TextField
            label="Image"
            value={item.image_url}
            onChange={(value) => patch({ image_url: value })}
            placeholder="https://…"
            testId={`slot-image-${item.id || "new"}`}
          />
        </div>
      </>
    )}

    <Toggle
      label="Actif"
      checked={item.active !== false}
      onChange={(value) => patch({ active: value })}
      testId={`slot-active-${item.id || "new"}`}
    />
  </>
);

const VariantRow = ({ row }) => (
  <li className="flex items-start justify-between gap-3 border-b py-2" style={{ borderColor: "var(--line)" }}>
    <span className="text-[13px] leading-snug">{row.text}</span>
    <span className="flex shrink-0 items-center gap-2">
      <button
        type="button"
        onClick={() => navigator.clipboard?.writeText(row.text).then(() => toast.success("Copié"))}
        aria-label="Copier"
        className="rounded p-1"
      >
        <Copy className="h-3.5 w-3.5" style={{ color: "var(--muted-ink)" }} />
      </button>
      <span
        className="font-num text-[11px]"
        style={{ color: row.trimmed ? "var(--warning)" : "var(--muted-ink)" }}
        title={row.trimmed ? "Le modèle avait dépassé la limite ; la variante a été recoupée." : ""}
      >
        {row.chars}/{row.limit}
      </span>
    </span>
  </li>
);

const CampaignStudio = ({ platforms }) => {
  const [brief, setBrief] = useState({
    platform: "google_search",
    lang: "fr",
    product: "",
    promise: "",
    audience: "",
    tone: "",
    avoid: "",
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [campaigns, setCampaigns] = useState([]);

  const refresh = () =>
    ownerCampaigns()
      .then((data) => setCampaigns(data.campaigns || []))
      .catch(() => setCampaigns([]));

  useEffect(() => {
    refresh();
  }, []);

  const platform = platforms.find((entry) => entry.id === brief.platform);

  const run = async () => {
    setBusy(true);
    try {
      setResult(await generateCreative(brief));
    } catch (error) {
      toast.error(errorMessage(error, "Les annonces n’ont pas pu être écrites"));
    } finally {
      setBusy(false);
    }
  };

  const keep = async () => {
    await saveCampaign({
      name: `${platform?.label} · ${new Date().toLocaleDateString("fr-FR")}`,
      platform: brief.platform,
      lang: brief.lang,
      brief,
      fields: result.fields,
    });
    toast.success("Campagne enregistrée");
    refresh();
  };

  return (
    <Panel
      title="Annonces pour vos campagnes"
      hint="Chaque plateforme compte les caractères, et chacune les compte différemment. Le modèle écrit, le code compte : une variante trop longue est recoupée au mot, jamais publiée telle quelle."
    >
      <div className="flex flex-wrap gap-2">
        {platforms.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => {
              setBrief({ ...brief, platform: entry.id });
              setResult(null);
            }}
            aria-pressed={brief.platform === entry.id}
            data-testid={`platform-${entry.id}`}
            className="rounded-full border px-3 py-1.5 text-[12.5px]"
            style={{
              borderColor: brief.platform === entry.id ? "var(--ink)" : "var(--line)",
              backgroundColor: brief.platform === entry.id ? "var(--ink)" : "transparent",
              color: brief.platform === entry.id ? "var(--paper)" : "var(--ink-soft)",
            }}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {platform ? (
        <p className="font-num mt-3 text-[11.5px] text-[color:var(--muted-ink)]">
          {platform.fields
            .map((field) => `${field.label} : ${field.count} × ${field.limit} car.`)
            .join(" · ")}
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextField
          label="Ce que c’est"
          value={brief.product}
          onChange={(value) => setBrief({ ...brief, product: value })}
          testId="brief-product"
        />
        <TextField
          label="Ce que ça change pour le lecteur"
          value={brief.promise}
          onChange={(value) => setBrief({ ...brief, promise: value })}
          testId="brief-promise"
        />
        <TextField
          label="À qui"
          value={brief.audience}
          onChange={(value) => setBrief({ ...brief, audience: value })}
          testId="brief-audience-ad"
        />
        <TextField
          label="Ton"
          value={brief.tone}
          onChange={(value) => setBrief({ ...brief, tone: value })}
          testId="brief-tone-ad"
        />
        <TextField
          label="À éviter"
          value={brief.avoid}
          onChange={(value) => setBrief({ ...brief, avoid: value })}
          testId="brief-avoid"
        />
        <div className="mt-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--muted-ink)]">
            Langue
          </p>
          <select
            value={brief.lang}
            onChange={(event) => setBrief({ ...brief, lang: event.target.value })}
            data-testid="brief-lang-ad"
            className="mt-1.5 h-10 w-full rounded-lg border bg-white px-2 text-[13.5px]"
            style={{ borderColor: "var(--line)" }}
          >
            <option value="fr">Français</option>
            <option value="en">English</option>
          </select>
        </div>
      </div>

      <Button onClick={run} disabled={busy} className="mt-5" data-testid="generate-creative">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
        Écrire les annonces
      </Button>

      {result ? (
        <div className="mt-6 rounded-lg border p-4" style={{ borderColor: "var(--line)" }}>
          {Object.entries(result.fields).map(([name, rows]) => {
            const spec = platform?.fields.find((field) => field.id === name);
            return (
              <div key={name} className="mt-4 first:mt-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--muted-ink)]">
                  {spec?.label || name}
                </p>
                <ul className="mt-1">
                  {rows.map((row, index) => (
                    <VariantRow key={`${name}-${index}`} row={row} />
                  ))}
                </ul>
              </div>
            );
          })}
          {result.trimmed ? (
            <p className="mt-3 text-[11.5px]" style={{ color: "var(--warning)" }}>
              {result.trimmed} variante(s) dépassaient la limite et ont été recoupées.
            </p>
          ) : null}
          <Button onClick={keep} size="sm" variant="outline" className="mt-4" data-testid="save-campaign">
            Garder cette campagne
          </Button>
        </div>
      ) : null}

      {campaigns.length ? (
        <div className="mt-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[color:var(--muted-ink)]">
            Campagnes gardées
          </p>
          <ul className="mt-2 divide-y" style={{ borderColor: "var(--line)" }}>
            {campaigns.map((campaign) => (
              <li key={campaign.id} className="flex items-center justify-between gap-3 py-2">
                <button
                  type="button"
                  onClick={() => setResult({ fields: campaign.fields, trimmed: 0 })}
                  className="min-w-0 flex-1 text-left text-[13px]"
                  data-testid={`campaign-${campaign.id}`}
                >
                  <span className="block truncate">{campaign.name}</span>
                  <span className="font-num text-[11px] text-[color:var(--muted-ink)]">
                    {campaign.platform} · {campaign.lang.toUpperCase()}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await removeCampaign(campaign.id);
                    refresh();
                  }}
                  aria-label="Supprimer"
                  className="rounded p-1"
                >
                  <Trash2 className="h-3.5 w-3.5" style={{ color: "var(--danger)" }} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Panel>
  );
};

export const AdsPanel = ({ draft, set }) => {
  const [catalogue, setCatalogue] = useState({ placements: [], platforms: [] });

  useEffect(() => {
    getPlacements()
      .then(setCatalogue)
      .catch(() => setCatalogue({ placements: [], platforms: [] }));
  }, []);

  const ads = draft.ads || {};

  return (
    <div className="space-y-6">
      <Panel
        title="Encarts sur le site"
        hint="Chaque encart est signalé « Sponsorisé » et son lien est marqué comme rémunéré. Ce n’est pas une politesse : une publicité non signalée est une pratique trompeuse."
      >
        <Toggle
          label="Afficher les encarts"
          hint="Désactivé, aucun encart n’est rendu, quel que soit leur nombre."
          checked={ads.enabled}
          onChange={(value) => set("ads.enabled", value)}
          testId="ads-enabled"
        />

        <div className="mt-6">
          <Repeater
            items={ads.slots || []}
            onChange={(next) => set("ads.slots", next)}
            blank={() => ({
              kind: "own",
              placement: "article_end",
              active: true,
              label: "",
              title: { fr: "", en: "" },
              body: { fr: "", en: "" },
              cta: { fr: "", en: "" },
              href: "",
              image_url: "",
            })}
            addLabel="Ajouter un encart"
            testId="ad-slots"
            renderItem={(item, patch) => (
              <SlotEditor item={item} patch={patch} placements={catalogue.placements} />
            )}
          />
        </div>
      </Panel>

      <CampaignStudio platforms={catalogue.platforms} />
    </div>
  );
};

export default AdsPanel;
